/**
 * Safe LocalStorage helpers with automatic QuotaExceededError recovery.
 * Automatically clears non-essential cache keys if storage quota is reached.
 */

const PRESERVED_KEYS = new Set(['token', 'tokenExpiry', 'sidenav:collapsed']);

export const clearNonEssentialStorage = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !PRESERVED_KEYS.has(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (_) {}
    });
    console.info(`[Storage] Cleared ${keysToRemove.length} non-essential cached items.`);
  } catch (err) {
    console.warn('[Storage] Failed to clear non-essential storage:', err);
  }
};

export const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    if (
      err.name === 'QuotaExceededError' ||
      err.code === 22 ||
      err.code === 1014 ||
      err.number === -2147024882
    ) {
      console.warn(`[Storage] Storage quota exceeded while setting '${key}'. Performing emergency cleanup...`);
      clearNonEssentialStorage();
      try {
        localStorage.setItem(key, value);
      } catch (retryErr) {
        console.error(`[Storage] Failed to set '${key}' even after storage cleanup:`, retryErr);
      }
    } else {
      console.error(`[Storage] Error setting '${key}':`, err);
    }
  }
};

export const safeGetItem = (key, defaultValue = null) => {
  try {
    return localStorage.getItem(key) ?? defaultValue;
  } catch (err) {
    console.warn(`[Storage] Error reading '${key}':`, err);
    return defaultValue;
  }
};

export const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn(`[Storage] Error removing '${key}':`, err);
  }
};
